function loadImage(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not load the uploaded image.'));
    };

    image.src = objectUrl;
  });
}

export async function processImageForPreview(file, targetWidth = 384, options = {}) {
  const image = await loadImage(file);
  const rotate90 = Boolean(options.rotate90);
  const sourceWidth = rotate90 ? image.height : image.width;
  const sourceHeight = rotate90 ? image.width : image.height;
  const scale = targetWidth / sourceWidth;
  const targetHeight = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    throw new Error('Could not create an image preview.');
  }

  ctx.imageSmoothingEnabled = true;
  if (rotate90) {
    ctx.translate(targetWidth, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(image, 0, 0, targetHeight, targetWidth);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  } else {
    ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
  }

  const imageData = ctx.getImageData(0, 0, targetWidth, targetHeight);
  const { data } = imageData;
  const width = targetWidth;
  const height = targetHeight;

  const grayscale = new Float32Array(width * height);

  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    grayscale[p] = 0.299 * r + 0.587 * g + 0.114 * b;
  }

  const clamp = (value) => Math.max(0, Math.min(255, value));

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = y * width + x;
      const oldPixel = grayscale[index];
      const newPixel = oldPixel < 128 ? 0 : 255;
      const error = oldPixel - newPixel;
      grayscale[index] = newPixel;

      if (x + 1 < width) grayscale[index + 1] = clamp(grayscale[index + 1] + (error * 7) / 16);
      if (x > 0 && y + 1 < height) grayscale[index + width - 1] = clamp(grayscale[index + width - 1] + (error * 3) / 16);
      if (y + 1 < height) grayscale[index + width] = clamp(grayscale[index + width] + (error * 5) / 16);
      if (x + 1 < width && y + 1 < height) grayscale[index + width + 1] = clamp(grayscale[index + width + 1] + error / 16);
    }
  }

  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    const value = grayscale[p] < 128 ? 0 : 255;
    data[i] = value;
    data[i + 1] = value;
    data[i + 2] = value;
    data[i + 3] = 255;
  }

  ctx.putImageData(imageData, 0, 0);

  return {
    previewUrl: canvas.toDataURL('image/png'),
    width: targetWidth,
    height: targetHeight,
  };
}
